/**
 * Account Management API Service
 * Invitation flow: GET/POST /accounts/invitation/{token}
 */

import { v4 as uuidv4 } from "uuid";
import iamHttps from "../../axios/iam";
import {
  GetInvitationAPIResponse,
  SetPasswordRequest,
  SetPasswordResponse,
} from "../types/accountManagement.types";
import { AxiosResponse } from "axios";

/** Build mandatory headers for Account Management API (X-Message-Id, X-Correlation-Id) */
function getMandatoryHeaders(): { "X-Message-Id": string; "X-Correlation-Id": string } {
  return {
    "X-Message-Id": uuidv4(),
    "X-Correlation-Id": uuidv4(),
  };
}

/**
 * Get invitation details by token (pre-fill account setup form)
 * GET /accounts/invitation/{token}
 */
export async function getInvitationByToken(
  token: string
): Promise<GetInvitationAPIResponse> {
  const url = `accounts/invitation/${token}`;
  const response: AxiosResponse<GetInvitationAPIResponse> =
    await iamHttps.get(url, { headers: getMandatoryHeaders() });
  return response.data;
}

/**
 * Set password and activate user/account by invitation token
 * POST /accounts/invitation/{token}
 */
export async function setPasswordByInvitationToken(
  token: string,
  body: SetPasswordRequest
): Promise<{ status: "SUCCESS" | "FAILURE"; statusinfo: { code: string; message: string }; data?: SetPasswordResponse }> {
  const url = `accounts/invitation/${token}`;
  try {
    const response = await iamHttps.post<SetPasswordResponse | { status?: string; statusinfo?: { message?: string }; data?: SetPasswordResponse }>(
      url,
      body,
      { headers: getMandatoryHeaders() }
    );
    const data = response.data;
    // API may return SetPasswordResponse directly or wrapped in status/data
    if (data && typeof data === "object" && (data as any).status === "FAILURE") {
      return {
        status: "FAILURE",
        statusinfo: {
          code: (data as any).statusinfo?.code || "ERROR",
          message: (data as any).statusinfo?.message || "Account setup failed.",
        },
      };
    }
    const payload = (data as any)?.data ?? data;
    return {
      status: "SUCCESS",
      statusinfo: { code: "200", message: (payload as SetPasswordResponse)?.message || "Account setup completed successfully." },
      data: payload as SetPasswordResponse,
    };
  } catch (error) {
    throw error;
  }
}
